import React, { useState } from "react";
import {
  Calendar,
  Download,
  FileText,
  AlertCircle,
  CheckCircle,
  X,
} from "lucide-react";
import { adminReportService } from "@/services/api/admin.report.service";

export default function FinancialReportsPanel({
  userId = null,
  userFullName = null,
}) {
  const [showReportModal, setShowReportModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [format, setFormat] = useState("PDF");
  const [includePortfolio, setIncludePortfolio] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const generateReport = async () => {
    if (!startDate || !endDate) {
      setError("Start date and end date are required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;

      if (userId) {
        // Generate report for a specific user
        response = await adminReportService.generateUserFinancialReport(
          userId,
          {
            startDate,
            endDate,
            format,
            includePortfolio,
          }
        );
      } else {
        // Generate report for all users
        response = await adminReportService.generateAllUsersFinancialReport({
          startDate,
          endDate,
          format,
          includePortfolio,
        });
      }

      setResult(response.data);
    } catch (err) {
      setError(err.message || "Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = (reportId) => {
    if (userId) {
      adminReportService.downloadReport(reportId);
    } else {
      adminReportService.downloadFinancialReport(reportId);
    }
  };

  const handleCloseModal = () => {
    setShowReportModal(false);
    // Clear previous results if modal is closed
    if (!loading) {
      setResult(null);
      setError(null);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowReportModal(true)}
        className="bg-blue-50 hover:bg-blue-100 text-blue-600 font-medium py-2 px-4 rounded-lg text-sm flex items-center transition-colors"
      >
        <FileText className="h-4 w-4 mr-2" />
        {userId
          ? `Generate Report for ${userFullName || "User"}`
          : "Generate All Users Financial Report"}
      </button>

      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">
                {userId
                  ? `Generate Financial Report for ${userFullName || "User"}`
                  : "Generate All Users Financial Report"}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 border border-red-100 rounded-md flex items-start">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                <p>{error}</p>
              </div>
            )}

            {result ? (
              <div className="mb-4 p-4 bg-green-50 border border-green-100 rounded-md">
                <div className="flex items-center text-green-700 mb-2">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  <span className="font-medium">
                    Report generation initiated!
                  </span>
                </div>

                {result.reportId && (
                  <div className="mt-4">
                   
                   
                  </div>
                )}

                {result.reports && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Report Summary:
                    </p>
                    <ul className="text-sm text-gray-700">
                      <li>Total Users: {result.totalUsers}</li>
                      <li>Successful: {result.successfulReports}</li>
                      <li>Failed: {result.failedReports}</li>
                    </ul>
                    <button
                      onClick={() => downloadReport(result.reportId)}
                      className="mt-3 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg text-sm flex items-center justify-center w-full"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download  Report
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  generateReport();
                }}
              >
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full p-2 pl-10 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                      <Calendar className="absolute top-2.5 left-3 h-5 w-5 text-gray-400" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full p-2 pl-10 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                      <Calendar className="absolute top-2.5 left-3 h-5 w-5 text-gray-400" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Format
                    </label>
                    <select
                      value={format}
                      onChange={(e) => setFormat(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="PDF">PDF</option>
                      <option value="EXCEL">Excel</option>
                    </select>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="includePortfolio"
                      checked={includePortfolio}
                      onChange={(e) => setIncludePortfolio(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="includePortfolio"
                      className="ml-2 block text-sm text-gray-700"
                    >
                      Include Portfolio Details
                    </label>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className={`w-full py-2 px-4 rounded-lg font-medium text-white ${
                        loading
                          ? "bg-blue-400 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700"
                      }`}
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                          Generating...
                        </div>
                      ) : (
                        "Generate Report"
                      )}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
